import type { CpcOutlook, HistoryDay, HistoryReport, OutlookCategory } from '../../shared/contract'
import { fillText } from '../../shared/text'
import { METRIC_UNITS } from '../../shared/units'

// ─── Dates (local calendar days, YYYY-MM-DD) ───────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0')

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`
}

export function daySpan(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1
}

const fmt = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' })
const at = (date: string) => new Date(`${date}T12:00:00Z`)

export const monthDay = (date: string) => fmt({ month: 'short', day: 'numeric' }).format(at(date))
export const weekdayShort = (date: string) => fmt({ weekday: 'short' }).format(at(date))
export const monthName = (date: string) => fmt({ month: 'long' }).format(at(date))

/** "Sep 23–25", "Sep 30–Oct 3", "Jan 1–10, 2024", "Dec 28, 2025–Jan 3, 2026". */
export function rangeLabel(start: string, end: string, today?: string): string {
  const year = (d: string) => d.slice(0, 4)
  const full = (d: string) => fmt({ month: 'short', day: 'numeric', year: 'numeric' }).format(at(d))
  if (year(start) !== year(end)) return `${full(start)}–${full(end)}`
  const suffix = today != null && year(end) !== year(today) ? `, ${year(end)}` : ''
  if (start === end) return `${monthDay(start)}${suffix}`
  const tail = start.slice(0, 7) === end.slice(0, 7) ? String(Number(end.slice(8))) : monthDay(end)
  return `${monthDay(start)}–${tail}${suffix}`
}

// ─── Range presets ─────────────────────────────────────────────────────────────

export type PresetId = 'last7' | 'last30' | 'month' | 'lastyear' | 'custom'

export interface Preset {
  id: PresetId
  label: string
  start: string
  end: string
}

/** The quick ranges, relative to the location's local "today" (history ends yesterday). */
export function presets(today: string): Preset[] {
  const yesterday = addDays(today, -1)
  // On the 1st, "this month" has no finished days yet: offer last month instead.
  const monthStart = today.endsWith('-01') ? `${addDays(today, -1).slice(0, 7)}-01` : `${today.slice(0, 7)}-01`
  const [y, m, d] = today.split('-').map(Number)
  const lastYear = `${y - 1}-${pad(m)}-${pad(m === 2 && d === 29 ? 28 : d)}`
  return [
    { id: 'last7', label: 'Last 7 days', start: addDays(today, -7), end: yesterday },
    { id: 'last30', label: 'Last 30 days', start: addDays(today, -30), end: yesterday },
    { id: 'month', label: monthName(monthStart), start: monthStart, end: yesterday },
    { id: 'lastyear', label: 'This week last year', start: addDays(lastYear, -3), end: addDays(lastYear, 3) },
  ]
}

export const MAX_DAYS = 366
export const EARLIEST = '1850-01-01'

/** An error message for a custom range, or null when it can be requested. */
export function rangeError(start: string, end: string, today: string): string | null {
  const ok = (d: string) => /^\d{4}-\d\d-\d\d$/.test(d) && Number.isFinite(Date.parse(`${d}T00:00:00Z`))
  if (!ok(start) || !ok(end)) return 'Choose both dates.'
  if (start < EARLIEST) return 'Records start in 1850.'
  if (end >= today) return 'Pick days before today — today is still in progress.'
  if (start > end) return 'The first day must come before the last.'
  if (daySpan(start, end) > MAX_DAYS) return `Choose at most ${MAX_DAYS} days.`
  return null
}

// ─── History helpers ───────────────────────────────────────────────────────────

export const hasRecord = (d: HistoryDay) =>
  d.high_c != null || d.low_c != null || d.precip_mm != null || d.snow_cm != null || d.wind_max_ms != null

/**
 * Days not published yet: the trailing run of empty days within the last ~6 weeks
 * (NOAA daily summaries arrive days to weeks late). Older gaps are simply missing.
 */
export function pendingDates(days: HistoryDay[]): Set<string> {
  // The engine marks each day; recent days the source hasn't published yet are 'pending'.
  return new Set(days.filter((d) => d.status === 'pending').map((d) => d.date))
}

/** Plain CSV (SI units, one row per day), ready to open in any spreadsheet. */
export function historyCsv(h: HistoryReport): string {
  const head = [
    'date',
    'high_c',
    'low_c',
    'precip_mm',
    'snow_cm',
    'wind_max_ms',
    'condition',
    'normal_high_c',
    'normal_low_c',
    'station_id',
    'source',
  ]
  const cell = (v: string | number | null) => {
    if (v == null) return ''
    const s = String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const rows = h.days.map((d) =>
    [d.date, d.high_c, d.low_c, d.precip_mm, d.snow_cm, d.wind_max_ms, d.condition, d.normal_high_c, d.normal_low_c, h.station?.id ?? null, fillText(h.source, METRIC_UNITS)]
      .map(cell)
      .join(','),
  )
  return [head.join(','), ...rows].join('\r\n') + '\r\n'
}

export function csvFilename(h: HistoryReport): string {
  const place = h.location.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'location'
  return `weather-history-${place}-${h.start}-to-${h.end}.csv`
}

// ─── Outlook wording ───────────────────────────────────────────────────────────

const LEVELS = [33, 40, 50, 60, 70, 80, 90, 100]

/** CPC probabilities are contour bands: 40 → "40–50%". Near normal is drawn at 33–40%. */
export function probabilityBand(p: number, category: OutlookCategory): string {
  const lower = category === 'near' ? 33 : p
  const upper = LEVELS.find((x) => x > lower) ?? 100
  return `${lower}–${upper}%`
}

export type OutlookKind = 'temperature' | 'precipitation'

export function outlookWords(v: CpcOutlook['temperature'], kind: OutlookKind): { headline: string; detail: string } {
  const noun = kind === 'temperature' ? 'temperatures' : 'precipitation'
  if (!v) return { headline: 'Not covered', detail: "This location is outside the outlook's map area." }
  if (v.category === 'equal_chances' || v.probability == null) {
    return { headline: 'Equal chances', detail: 'No lean: above, near and below normal equally likely' }
  }
  const lean =
    v.category === 'near'
      ? 'Leans near normal'
      : kind === 'temperature'
        ? v.category === 'above'
          ? 'Leans warmer'
          : 'Leans cooler'
        : v.category === 'above'
          ? 'Leans wetter'
          : 'Leans drier'
  const cat = v.category === 'near' ? 'near-normal' : `${v.category}-normal`
  return { headline: lean, detail: `${probabilityBand(v.probability, v.category)} chance of ${cat} ${noun}` }
}

export const PERIOD_LABEL: Record<CpcOutlook['period'], string> = {
  '6-10 day': '6–10 day outlook',
  '8-14 day': '8–14 day outlook',
  'week 3-4': 'Week 3–4 outlook',
}
